import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output } from '@angular/core'

/**
 * Component to render content in a drawer.
 */
@Component({
    selector: 'app-drawer',
    templateUrl: './drawer.component.html',
    styleUrls: ['./drawer.component.scss'],
})
export class DrawerComponent implements AfterViewInit, OnDestroy {

    private boundClickListener!: (e: Event) => void;

    /**
     * Apply class to host element
     */
    @HostBinding('class.drawer')
    drawerClass: boolean = true

    /**
     * Apply class to host element when drawer opened
     */
    @HostBinding('class.opened')
    @Input()
    opened: boolean = false

    /**
     * Apply class to host element when drawer closed
     */
    @HostBinding('class.closed')
    get closed(): boolean {
        return !this.opened
    }

    /**
     * Reference of the trigger element eg. #filterTrigger
     */
    @Input()
    trigger: any

    /**
     * Event emitter to update the parent element whenever the drawer is closed
     * from within here eg. close icon
     */
    @Output()
    openedChange = new EventEmitter<boolean>()

    /**
     * Click detection inside layout
     */
    private onClick(e: Event): void {
        const target = e.target as HTMLElement;

        if (this._triggerCheck(target)) {
            if (this.opened && !this._contains(target)) {
                this.close();
            }
        }
    }

    /**
     * Localized close label
     */
    CLOSE_LOCALIZED: string = $localize`:Close label text|close label text @@Close:Close`;

    constructor(private _eRef: ElementRef) { }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngAfterViewInit(): void {
        this.boundClickListener = this.onClick.bind(this);
        const layoutEl = document.querySelector('layout');

        if (layoutEl) {
            layoutEl.addEventListener('click', this.boundClickListener);
        }
    }

    ngOnDestroy(): void {
        const layoutEl = document.querySelector('layout');

        if (layoutEl && this.boundClickListener) {
            layoutEl.removeEventListener('click', this.boundClickListener);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    close(): void {
        this.opened = false
        this.openedChange.emit(false)
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Checks whether mouse click event is fired within the host element
     */
    private _contains(target: EventTarget | null): boolean {
        return this._eRef.nativeElement.contains(target);
    }

    /**
     * Checks whether the drawer is opened by a trigger
     */
    private _triggerCheck(target: EventTarget | null): boolean {
        if (this.trigger?._elementRef) {
            return target !== this.trigger._elementRef.nativeElement;
        } else {
            return target !== this.trigger;
        }
    }
}
